import StateObserver from 'src/StateObserver';
import * as Gifts from 'src/replicant/gifts';
import { GiftType } from 'src/replicant/gifts';

export type CollectedGift = { gift: GiftType; amount: number; sender: string };

export type GiftBatchResult = {
  /** Inbound: gifts claimed from friends, with the reward each one gave. */
  collected: CollectedGift[];
  /** Outbound: friends sent a gift, and who should therefore be notified. */
  sentIds: string[];
  /** Set when a batch failed. Earlier batches are still in the other two. */
  error?: unknown;
};

/** Both actions ride a single flush, so a batch lands or fails as a unit. */
export type Batch = { collected: CollectedGift[]; sentIds: string[] };

type ResolveGiftsArgs = {
  /** Senders whose received gifts should be collected. */
  collectIds: string[];
  /** Friends who should receive a gift, in the caller's priority order. */
  sendIds: string[];
  /** Called with each batch as it is confirmed, for incremental UI/analytics. */
  onBatchConfirmed?: (batch: Batch) => void;
};

let running = false;

/**
 * True while a run is in flight.
 *
 * A run outlives the popup that started it, so a popup opened mid-run has no other
 * way to know its send button would do nothing.
 */
export function isResolvingGifts(): boolean {
  return running;
}

/**
 * Runs are mutually exclusive, and the latch is module-scoped because the run is.
 *
 * Every UI guard belongs to a popup instance, and a run outlives that instance, so
 * closing the popup mid-run and reopening it produces fresh guards that know nothing
 * about the run still going. The reopened list is recomputed from optimistic state
 * the running loop has already stamped, which makes it exactly that loop's next
 * chunk — so a second run submits ids the first has in flight, and the server skips
 * every one of them. See #11707.
 */
export function resolveGiftsInBatches(
  args: ResolveGiftsArgs,
): Promise<GiftBatchResult> {
  if (running) return Promise.resolve({ collected: [], sentIds: [] });

  // Set before the run starts, not after. A sync action applies and notifies state
  // listeners synchronously, inside the first invoke, so a latch assigned from the
  // returned promise would still read false for the first batch.
  running = true;

  return runGiftBatches(args).finally(() => {
    running = false;
  });
}

/**
 * Collect and send gifts in bounded batches, so one tap never fans out over a whole
 * friend list in a single replication request (#11690).
 *
 * The flush is what bounds it. A sync invoke resolves on the local optimistic
 * apply, before any network I/O, so awaiting invokes alone just refills one batch.
 *
 * Confirmation lags a batch behind. `flush()` resolves even when replication
 * failed, so a batch is only known to have landed once a later invoke resolves — a
 * failed one puts the client out of sync and the next invoke rejects. Without this,
 * `sentIds` would name recipients whose gift was never written.
 *
 * Runs to completion independently of the UI that started it. A single-request send
 * already survived the gifts popup closing, and batching must not change that.
 */
async function runGiftBatches(
  args: ResolveGiftsArgs,
): Promise<GiftBatchResult> {
  const { collectIds, sendIds } = args;

  const { giftBatchSize } = Gifts.ruleset;
  const batchCount = Math.ceil(
    Math.max(collectIds.length, sendIds.length) / giftBatchSize,
  );

  const collected: CollectedGift[] = [];
  const sentIds: string[] = [];

  // Held back until a later invoke proves it replicated.
  let unconfirmed: Batch | undefined;

  const confirmPrevious = () => {
    const batch = unconfirmed;
    if (!batch) return;

    // Cleared first, so a repeat call is a no-op whatever onBatchConfirmed does.
    unconfirmed = undefined;

    collected.push(...batch.collected);
    sentIds.push(...batch.sentIds);
    args.onBatchConfirmed?.(batch);
  };

  try {
    for (let index = 0; index < batchCount; index += 1) {
      const from = index * giftBatchSize;
      const collectBatch = collectIds.slice(from, from + giftBatchSize);
      const sendBatch = sendIds.slice(from, from + giftBatchSize);

      const batch: Batch = { collected: [], sentIds: [] };

      // These confirm the PREVIOUS batch, not this one. An invoke resolving is the
      // only proof the client is still in sync, so it is what clears the batch
      // before it. Doing this after the first invoke rather than after both means a
      // rejecting second invoke cannot discard a batch that already landed.
      if (collectBatch.length) {
        batch.collected = await StateObserver.invoke.consumeAllGifts({
          senderIds: collectBatch,
          // Cleanup prunes the whole received map, so it must not run while a later
          // batch still has gifts to collect. Keyed off collectIds, not the index:
          // batchCount follows the longer list, which is usually sendIds.
          isLastBatch: from + giftBatchSize >= collectIds.length,
        });
        confirmPrevious();
      }

      if (sendBatch.length) {
        // Returns what it actually wrote; targets can stop being sendable.
        batch.sentIds = await StateObserver.invoke.sendGiftsMessages(sendBatch);
        confirmPrevious();
      }

      unconfirmed = batch;

      await StateObserver.replicant.flush();
    }
  } catch (error) {
    // The failure surfaces a batch late, so the unconfirmed one is the bad one.
    return { collected, sentIds, error };
  }

  // Nothing follows the last batch to confirm it, so accept it.
  confirmPrevious();

  return { collected, sentIds };
}
